import type { RouteRecordRaw } from 'vue-router';

export interface MenuItem {
  title: string; // 已解析的展示标题（可能是原始或翻译后）
  titleKey?: string; // 原始国际化 key，便于后续语言切换时重新映射
  path: string;
  icon?: string;
  children?: MenuItem[];
  order?: number;
  affix?: boolean;
}

function sortMenus(list: MenuItem[]): MenuItem[] {
  return list.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
}

export function buildMenus(routes: RouteRecordRaw[]): MenuItem[] {
  const result: MenuItem[] = [];

  routes.forEach((route) => {
    // 如果路由隐藏则跳过
    if (route.meta?.hidden) return;

    const titleKey = route.meta?.titleKey;
    const title = route.meta?.title;

    // 如果没有标题
    if (!title) {
      // 没有标题且不是根节点则跳过，但继续处理其子节点（展开提升）
      if (route.children) {
        const childMenus = buildMenus(route.children);
        childMenus.forEach((c) => result.push(c));
      }
      return;
    }

    const item: MenuItem = {
      title: titleKey || title || '',
      titleKey,
      path: route.path.startsWith('/') ? route.path : `/${route.path}`,
      icon: route.meta?.icon,
      order: route.meta?.order,
      affix: route.meta?.affix,
    };

    if (route.children) {
      const children = buildMenus(route.children);
      // 过滤掉一级菜单没有component属性且children为空的情况
      if (children.length) {
        item.children = sortMenus(children);
      } else if (!route.component) {
        // 如果一级菜单没有component且没有有效子菜单，则跳过该项
        return;
      }
    } else if (!route.component) {
      // 如果既没有component也没有children，则跳过该项
      return;
    }

    result.push(item);
  });

  return sortMenus(result);
}

/**
 * 根据菜单列表解析首页路径规则：
 * 1) 若无菜单返回 null
 * 2) 使用第一个菜单项；如果它有 children，取第一个子菜单拼接形成路径
 * 3) 返回标准化(去除多余斜杠)后的路径字符串
 */
export function resolveHomePath(menus: MenuItem[]): string | null {
  if (!menus || menus.length === 0) return null;
  const first = menus[0];
  if (first.children && first.children.length > 0) {
    return (
      first.path.replace(/\/+$/, '') +
      '/' +
      first.children[0].path.replace(/^\/+/, '')
    ).replace(/\/+/, '/');
  }
  return first.path;
}
